import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useUserAuth } from '../context/UserAuthContext';
import { IMAGE_BASE_URL, POSTER_SIZE } from '../config';
import NoImage from '../images/no_image.jpg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// ─── Stat helpers ─────────────────────────────────────────────────────────────

const topN = (arr, n = 3) => {
  const counts = {};
  arr.forEach((v) => { if (v) counts[v] = (counts[v] || 0) + 1; });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, n).map(([k]) => k);
};

const computeStats = (films) => {
  const genres    = topN(films.flatMap((f) => f.genres    || []));
  const directors = topN(films.flatMap((f) => f.directors || []));
  const actors    = topN(films.flatMap((f) => f.actors    || []));
  const rated     = films.filter((f) => f.userRating != null);
  const avgRating = rated.length
    ? (rated.reduce((s, f) => s + f.userRating, 0) / rated.length).toFixed(1)
    : null;
  return { total: films.length, genres, directors, actors, avgRating };
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`to { transform: rotate(360deg); }`;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  padding-bottom: 60px;
`;

/* ── Top bar ── */
/* ── Content ── */
const Content = styled.main`
  max-width: 1100px;
  margin: 0 auto;
  padding: 24px 16px 0;
  animation: ${fadeUp} 0.35s ease;

  @media (min-width: 600px) { padding: 32px 24px 0; }
  @media (min-width: 900px) { padding: 40px 32px 0; }
`;

const PageTitle = styled.h1`
  color: #fff;
  font-size: 1.45rem;
  font-weight: 800;
  margin: 0 0 24px;
  letter-spacing: -0.5px;
  span { color: #fe88f1; }

  @media (min-width: 600px) { font-size: 1.7rem; margin: 0 0 32px; }
`;

/* ── Stats row (horizontal scroll on mobile) ── */
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: 480px) { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 900px) { grid-template-columns: repeat(4, 1fr); margin-bottom: 28px; }
`;

const StatCard = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 12px;
  padding: 16px;
  animation: ${fadeUp} 0.4s ease both;
  animation-delay: ${p => p.$delay || '0s'};
`;

const StatLabel = styled.p`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.35);
  margin: 0 0 6px;
`;

const StatValue = styled.p`
  font-size: 1.7rem;
  font-weight: 800;
  color: #fff;
  margin: 0;
  line-height: 1;

  @media (min-width: 600px) { font-size: 2rem; }
`;

const StatSub = styled.p`
  font-size: 0.7rem;
  color: rgba(255,255,255,0.3);
  margin: 4px 0 0;
`;

/* ── Tag pills panel ── */
const TagsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: 700px) { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 900px) { margin-bottom: 28px; }
`;

const TagCard = styled.div`
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 12px;
  padding: 16px 18px;
  animation: ${fadeUp} 0.45s ease both;
  animation-delay: ${p => p.$delay || '0s'};
`;

const TagCardTitle = styled.p`
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.35);
  margin: 0 0 10px;
`;

const PillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Pill = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  padding: 5px 11px;
  background: rgba(254,136,241,0.12);
  color: #fe88f1;
  border-radius: 99px;
  border: 1px solid rgba(254,136,241,0.15);
`;

/* ── Film list panel ── */
const FilmsPanel = styled.section`
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 16px;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: 0.1s;

  @media (min-width: 600px) { padding: 24px 26px; }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  flex-wrap: wrap;
  gap: 8px;
`;

const PanelTitle = styled.h2`
  font-size: 0.85rem;
  font-weight: 700;
  color: rgba(255,255,255,0.5);
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin: 0;
`;

const FilmCount = styled.span`
  background: rgba(255,255,255,0.07);
  border-radius: 99px;
  padding: 2px 9px;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.5);
  font-weight: 700;
`;

const FilmList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilmItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  padding: 10px 12px;
  transition: background 0.15s;

  &:hover { background: rgba(255,255,255,0.055); }

  @media (min-width: 600px) { gap: 14px; padding: 12px 14px; }
`;

const Poster = styled.img`
  width: 38px;
  height: 56px;
  object-fit: cover;
  border-radius: 5px;
  flex-shrink: 0;

  @media (min-width: 600px) { width: 44px; height: 64px; border-radius: 6px; }
`;

const FilmMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const FilmTitle = styled.p`
  color: #fff;
  font-size: 0.88rem;
  font-weight: 600;
  margin: 0 0 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 600px) { font-size: 0.92rem; }
`;

const FilmSub = styled.p`
  color: rgba(255,255,255,0.32);
  font-size: 0.72rem;
  margin: 0;
  line-height: 1.4;
`;

const FilmRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const RatingBadge = styled.span`
  background: rgba(255,255,255,0.07);
  color: rgba(255,255,255,0.75);
  font-size: 0.74rem;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 6px;
  white-space: nowrap;

  /* Hide on very small phones to save space */
  @media (max-width: 360px) { display: none; }
`;

const RemoveBtn = styled.button`
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255,126,126,0.22);
  background: rgba(255,126,126,0.07);
  color: #ff7e7e;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;
  &:hover { opacity: 0.7; }

  @media (min-width: 600px) { font-size: 0.75rem; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: rgba(255,255,255,0.25);
  font-size: 0.88rem;
  line-height: 1.8;

  a { color: #fe88f1; text-decoration: none; }
  a:hover { text-decoration: underline; }
`;

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const SpinnerEl = styled.div`
  border: 2px solid rgba(254,136,241,0.15);
  border-top-color: #fe88f1;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  animation: ${spin} 0.8s linear infinite;
`;

/* ── Preferences panel ── */
const PrefsPanel = styled.section`
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 14px;
  padding: 20px;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: 0.15s;

  @media (min-width: 600px) { padding: 24px 26px; }
`;

const PrefRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255,255,255,0.06);
`;

const PrefLabel = styled.div`
  flex: 1;
  p:first-child {
    font-size: 0.88rem;
    font-weight: 600;
    color: rgba(255,255,255,0.75);
    margin: 0 0 3px;
  }
  p:last-child {
    font-size: 0.74rem;
    color: rgba(255,255,255,0.28);
    margin: 0;
    line-height: 1.5;
  }
`;

const ToggleWrap = styled.label`
  position: relative;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
`;

const ToggleSlider = styled.span`
  position: absolute;
  inset: 0;
  background: ${p => p.$on ? '#fe88f1' : 'rgba(255,255,255,0.12)'};
  border-radius: 24px;
  transition: background 0.2s;
  &::before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    left: ${p => p.$on ? '22px' : '3px'};
    top: 3px;
    background: #fff;
    border-radius: 50%;
    transition: left 0.2s;
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const UserDashboard = () => {
  const { isLoggedIn, user, userToken, showAdultContent, toggleAdultContent } = useUserAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn) navigate('/sign-in', { replace: true });
  }, [isLoggedIn, navigate]);

  const [films, setFilms]     = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeader = { Authorization: `Bearer ${userToken}` };

  const fetchFilms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/my-films`, { headers: authHeader });
      if (res.ok) setFilms(await res.json());
    } catch { /* silent */ }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  useEffect(() => { fetchFilms(); }, [fetchFilms]);

  const handleRemove = async (tmdbId) => {
    try {
      await fetch(`${BACKEND_URL}/my-films/${tmdbId}`, { method: 'DELETE', headers: authHeader });
      setFilms((prev) => prev.filter((f) => f.tmdbId !== tmdbId));
    } catch { /* silent */ }
  };

  const stats = computeStats(films);
  if (!isLoggedIn) return null;

  const displayName = user?.name || user?.email || 'there';

  return (
    <PageWrapper>

      <Helmet>
        <title>My Films — MyCinemaDB</title>
        <meta name="description" content="Your personal film collection on MyCinemaDB." />
      </Helmet>

      <Content>
        <PageTitle>My <span>Films</span></PageTitle>

        {/* ── Stats cards row ── */}
        <StatsRow>
          <StatCard $delay="0s">
            <StatLabel>Total Saved</StatLabel>
            <StatValue>{stats.total}</StatValue>
          </StatCard>

          {stats.avgRating && (
            <StatCard $delay="0.05s">
              <StatLabel>Avg Rating</StatLabel>
              <StatValue>{stats.avgRating}</StatValue>
              <StatSub>out of 10</StatSub>
            </StatCard>
          )}

          {stats.genres[0] && (
            <StatCard $delay="0.1s">
              <StatLabel>Top Genre</StatLabel>
              <StatValue style={{ fontSize: '1.1rem', paddingTop: 4 }}>{stats.genres[0]}</StatValue>
            </StatCard>
          )}

          {stats.directors[0] && (
            <StatCard $delay="0.15s">
              <StatLabel>Top Director</StatLabel>
              <StatValue style={{ fontSize: '0.95rem', paddingTop: 4, lineHeight: 1.2 }}>
                {stats.directors[0]}
              </StatValue>
            </StatCard>
          )}
        </StatsRow>

        {/* ── Genre / Director / Actor pills ── */}
        {(stats.genres.length > 0 || stats.directors.length > 0 || stats.actors.length > 0) && (
          <TagsGrid>
            {stats.genres.length > 0 && (
              <TagCard $delay="0.1s">
                <TagCardTitle>Favorite Genres</TagCardTitle>
                <PillList>{stats.genres.map((g) => <Pill key={g}>{g}</Pill>)}</PillList>
              </TagCard>
            )}
            {stats.directors.length > 0 && (
              <TagCard $delay="0.15s">
                <TagCardTitle>Top Directors</TagCardTitle>
                <PillList>{stats.directors.map((d) => <Pill key={d}>{d}</Pill>)}</PillList>
              </TagCard>
            )}
            {stats.actors.length > 0 && (
              <TagCard $delay="0.2s">
                <TagCardTitle>Top Actors</TagCardTitle>
                <PillList>{stats.actors.map((a) => <Pill key={a}>{a}</Pill>)}</PillList>
              </TagCard>
            )}
          </TagsGrid>
        )}

        {/* ── Film list ── */}
        <FilmsPanel>
          <PanelHeader>
            <PanelTitle>Saved Films</PanelTitle>
            <FilmCount>{films.length}</FilmCount>
          </PanelHeader>

          {loading ? (
            <SpinnerWrap><SpinnerEl /></SpinnerWrap>
          ) : films.length === 0 ? (
            <EmptyState>
              You haven't saved any films yet.<br />
              <Link to="/">Browse movies</Link> and click <strong>Save to your database</strong>.
            </EmptyState>
          ) : (
            <FilmList>
              {films.map((film) => (
                <FilmItem key={film.tmdbId}>
                  <Poster
                    src={film.posterPath ? `${IMAGE_BASE_URL}${POSTER_SIZE}${film.posterPath}` : NoImage}
                    alt={film.title}
                  />
                  <FilmMeta>
                    <FilmTitle>{film.title}</FilmTitle>
                    <FilmSub>{(film.genres || []).slice(0, 2).join(' · ')}</FilmSub>
                    {(film.directors || []).length > 0 && (
                      <FilmSub>{film.directors[0]}</FilmSub>
                    )}
                  </FilmMeta>
                  <FilmRight>
                    {film.voteAverage > 0 && (
                      <RatingBadge>⭐ {film.voteAverage}</RatingBadge>
                    )}
                    <RemoveBtn onClick={() => handleRemove(film.tmdbId)}>Remove</RemoveBtn>
                  </FilmRight>
                </FilmItem>
              ))}
            </FilmList>
          )}
        </FilmsPanel>

        {/* ── Preferences ── */}
        <PrefsPanel>
          <PanelTitle>Display Preferences</PanelTitle>
          <PrefRow>
            <PrefLabel>
              <p>Adult Content</p>
              <p>
                When enabled, movies flagged as adult content by TMDB will appear in
                search results and on the home page. Must be 18+.
              </p>
            </PrefLabel>
            <ToggleWrap htmlFor="adult-toggle">
              <ToggleInput
                id="adult-toggle"
                type="checkbox"
                checked={showAdultContent}
                onChange={(e) => toggleAdultContent(e.target.checked)}
              />
              <ToggleSlider $on={showAdultContent} />
            </ToggleWrap>
          </PrefRow>
        </PrefsPanel>

      </Content>
    </PageWrapper>
  );
};

export default UserDashboard;
